import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { env } from "@/shared/config/env";
import { NAV_GROUPS } from "@/shared/lib/navItems";
import { featureOn } from "@/shared/auth/features";
import { hasMinRole } from "@/shared/auth/permissions";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useState } from "react";
import { AccountMenu } from "@/widgets/AccountMenu";
import { UtilityCluster } from "@/widgets/UtilityCluster";

/**
 * Desktop primary navigation. The menu itself lives in shared/lib/navItems
 * so the mobile sheet renders the identical list — this file is only the
 * desktop presentation of it.
 */
export function Sidebar({ activePath }: { activePath?: string } = {}) {
  const location = useLocation();
  const pathname = activePath ?? location.pathname;
  const onControl = pathname === "/" || pathname === "/__preview/control";
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const { t } = useTranslation();
  const user = useAuth(selectUser);
  const role = user?.role;

  // Hide items above the user's role (and items for features the org has turned
  // off), then drop any group left empty so we don't render an orphan title/divider.
  const visibleGroups = NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          (!it.minRole || hasMinRole(role, it.minRole)) &&
          (!it.feature || featureOn(user, it.feature)),
      ),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <aside
      aria-label="Primary"
      className={cn(
        "garam-sidebar h-full w-[var(--sidebar-w)] shrink-0",
        "bg-[var(--bg)] border-r border-[var(--separator)]",
        "flex flex-col",
      )}
    >
      <div className="h-[60px] shrink-0 px-4 flex flex-col justify-center gap-0.5">
        <img src="/design/nav-garam-logo.png" alt={t("common.brand.alt")} width={67} height={20} className="egas-brand-img object-contain self-start" />
        <p className="text-[10px] leading-[1.4] font-normal text-[var(--fg-muted)] break-keep">
          {t("common.productName")}
        </p>
      </div>

      {/* ── Navigation ───────────────────────────────────────────── */}
      <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {visibleGroups.map((g, gi) => (
          <div key={gi} className={cn(!g.titleKey && "border-t border-[var(--separator)]")}>
            {g.titleKey && (
              <button type="button" className="w-full h-9 flex items-center justify-between border-b border-[var(--separator)] text-[13.5px] font-medium text-[var(--fg-subtle)]" aria-expanded={!collapsed.has(g.titleKey)} onClick={() => setCollapsed((prev) => {
                const next = new Set(prev);
                if (next.has(g.titleKey)) next.delete(g.titleKey); else next.add(g.titleKey);
                return next;
              })}>
                {t(g.titleKey)}
                <img src="/design/nav-arrow-drop-down.svg" alt="" width={16} height={16} className={cn(collapsed.has(g.titleKey) && "-rotate-90")} />
              </button>
            )}
            <ul className={cn(collapsed.has(g.titleKey) && "hidden")}>
              {g.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.to === "/"}
                    // The 2-pixel active rail is rendered via the
                    // `before:` pseudo-element so we don't need NavLink's
                    // children render-prop. The render-prop pattern can
                    // produce mismatched hook counts when StrictMode
                    // replays the render.
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 pl-5 pr-2.5 min-h-[38px] py-1",
                        "text-[13px] tracking-normal",
                        "transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-soft)]",
                        "before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2px] before:rounded-r-full",
                        (activePath ? pathname === it.to : (isActive || (onControl && it.to === "/")))
                          ? "bg-[var(--nav-selected-bg)] text-[var(--fg)] font-bold"
                          : "text-[var(--fg)] font-medium hover:bg-[var(--bg-hover)] before:bg-transparent",
                      )
                    }
                  >
                    {/* Tinted by group, not by status — see --nav-* in
                        tokens.css. The hue stays constant through the active
                        state: "where am I" is already carried by the rail,
                        the filled row and the bolder label, so recolouring
                        the icon too would only blur the group identity. */}
                    <img src={`/design/nav-icon-${NAV_ASSETS[it.to]}.svg`} alt="" width={16} height={16} className="garam-nav-icon shrink-0" />
                    {/* 메뉴명은 말줄임하지 않는다(2026-10-22, EN 「Anomaly Detec…」) — 길면 두 줄로 접고 줄 높이만 늘어난다. */}
                    <span className="flex-1 min-w-0 leading-[1.25] break-keep [overflow-wrap:anywhere]">{t(it.labelKey)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* ── Account footer ───────────────────────────────────────── */}
      <div className="p-2 border-t border-[var(--separator)]">
        {/* 언어·테마·음성·경보 — 상단바가 없어진 데스크톱의 자리. 통합 관제는 헤더에 같은
            묶음이 있으니 거기서는 숨긴다(두 벌은 어지럽다). */}
        {!onControl && <UtilityCluster compact className="mb-1 justify-between" />}
        <AccountMenu />
        {/* 버전·빌드번호 상시 표시 (env.build = VITE_BUILD_ID, 빌드 시 주입). */}
        <div className="px-2.5 pt-1.5 text-[10.5px] tabular-nums text-[var(--fg-subtle)] truncate">
          v{env.version} · {env.build}
        </div>
      </div>
    </aside>
  );
}


const NAV_ASSETS: Readonly<Record<string, string>> = {
  "/": "layout-grid", "/sites": "map", "/sensors": "activity",
  "/risk": "shield-alert", "/anomaly": "radar", "/dispersion": "wind",
  "/alerts": "triangle-alert", "/evacuation": "gauge", "/work-entries": "clipboard-list", "/devices": "siren",
  "/scenarios": "graduation-cap", "/kpi": "award", "/reports": "file-text", "/settings": "settings",
};
